import React, { useEffect, useState } from 'react';
import { Input, TextArea, Switch, Toast, Spin, Button, Modal, InputNumber, Tag, Tabs, Radio, Select, Table } from '@douyinfe/semi-ui';
import { API, copy } from '../helpers';

const RadioGroup = Radio.Group;

const { TabPane } = Tabs;

const AdminSettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // 发卡密钥状态
  const [dk, setDk] = useState(null);
  const [newKeyShown, setNewKeyShown] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [clearing, setClearing] = useState(false);

  // 数据维护
  const [cleanupInfo, setCleanupInfo] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  // 数据备份
  const [backupOv, setBackupOv] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupRestoring, setBackupRestoring] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [backupCfgVisible, setBackupCfgVisible] = useState(false);
  const [savingBackupCfg, setSavingBackupCfg] = useState(false);

  // 自动补货配置
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [draftAuto, setDraftAuto] = useState(false);
  const [draftWin, setDraftWin] = useState(60);
  const [draftMax, setDraftMax] = useState(10);

  const FORM_KEYS = ['site_name', 'announcement', 'public_url', 'default_user_quota', 'session_retention_days', 'log_retention_days', 'unsold_token_retention_days', 'support_enabled', 'support_content', 'quota_reset_week_day', 'quota_reset_week_hour'];

  useEffect(() => {
    API.get('/api/admin/settings')
      .then((r) => setData(r.data.data))
      .finally(() => setLoading(false));
    loadDeliveryKey();
    loadDeliveryConfig();
    loadCleanupInfo();
    loadBackupInfo();
  }, []);

  const loadBackupInfo = () => {
    Promise.all([
      API.get('/api/admin/backup/overview'),
      API.get('/api/admin/backup/list'),
    ])
      .then(([a, b]) => {
        setBackupOv(a.data.data || null);
        setBackupList(b.data.data || []);
      })
      .catch(() => { setBackupOv(null); setBackupList([]); });
  };

  const saveBackupCfg = async () => {
    setSavingBackupCfg(true);
    try {
      await Promise.all([
        API.put('/api/admin/settings', { key: 'backup_enabled', value: v('backup_enabled', '1') === '1' ? '1' : '0' }),
        API.put('/api/admin/settings', { key: 'backup_interval_days', value: String(v('backup_interval_days', '7')) }),
        API.put('/api/admin/settings', { key: 'backup_keep_count', value: String(v('backup_keep_count', '10')) }),
      ]);
      Toast.success('已保存');
      setBackupCfgVisible(false);
      loadBackupInfo();
    } catch (e) {
      Toast.error(e.response?.data?.message || '保存失败');
    } finally {
      setSavingBackupCfg(false);
    }
  };

  const runBackupNow = async () => {
    setBackupRunning(true);
    try {
      const r = await API.post('/api/admin/backup/run');
      Toast.success(`备份完成:${r.data.data.filename}`);
      loadBackupInfo();
    } catch (e) {
      Toast.error(e.response?.data?.message || '备份失败');
    } finally {
      setBackupRunning(false);
    }
  };

  const doRestore = async () => {
    if (!restoreTarget) return;
    setBackupRestoring(true);
    try {
      const r = await API.post('/api/admin/backup/restore', { id: restoreTarget.id, confirm: true });
      Toast.success(`已恢复,恢复前快照:${r.data.data.safety_backup}`);
      setRestoreTarget(null);
      loadBackupInfo();
    } catch (e) {
      Toast.error(e.response?.data?.message || '恢复失败');
    } finally {
      setBackupRestoring(false);
    }
  };

  const removeBackup = (row) => {
    Modal.confirm({
      title: '删除该备份?',
      content: `备份文件 ${row.filename} 将被一并删除,删除后无法恢复。`,
      okType: 'danger',
      onOk: async () => {
        try {
          await API.delete('/api/admin/backup/' + row.id);
          Toast.success('已删除');
          loadBackupInfo();
        } catch (e) {
          Toast.error(e.response?.data?.message || '删除失败');
        }
      },
    });
  };

  const loadCleanupInfo = () => {
    API.get('/api/admin/cleanup/overview')
      .then((r) => setCleanupInfo(r.data.data))
      .catch(() => setCleanupInfo(null));
  };

  const runCleanupNow = async () => {
    setCleanupRunning(true);
    try {
      await API.post('/api/admin/cleanup/run');
      Toast.success('已执行清理');
      loadCleanupInfo();
    } catch (e) {
      Toast.error(e.response?.data?.message || '清理失败');
    } finally {
      setCleanupRunning(false);
    }
  };

  const loadDeliveryKey = () => {
    API.get('/api/admin/delivery-key')
      .then((r) => setDk(r.data.data))
      .catch(() => setDk({ enabled: false, masked: '', has_key: false, stock: 0 }));
  };

  const loadDeliveryConfig = () => {
    API.get('/api/admin/delivery-config')
      .then((r) => {
        const d = r.data.data;
        setCfg(d);
        setDraftAuto(!!d.auto_create);
        setDraftWin(d.rate_window);
        setDraftMax(d.rate_max);
      })
      .catch(() => setCfg({ auto_create: false, rate_window: 60, rate_max: 10, rate_remaining: 10, plans: [] }));
  };

  const saveDeliveryConfig = async () => {
    setSavingCfg(true);
    try {
      await API.put('/api/admin/delivery-config', {
        auto_create: draftAuto,
        rate_window: draftWin,
        rate_max: draftMax,
      });
      Toast.success('已保存');
      loadDeliveryConfig();
    } catch (e) {
      Toast.error(e.response?.data?.message || '保存失败');
    } finally {
      setSavingCfg(false);
    }
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const submits = FORM_KEYS
        .filter((k) => data[k] !== undefined && data[k] !== null)
        .map((k) => {
          let v = data[k];
          if (typeof v === 'boolean') v = v ? '1' : '0';
          else v = String(v);
          return API.put('/api/admin/settings', { key: k, value: v });
        });
      await Promise.all(submits);
      Toast.success('已保存');
    } catch (e) {
      Toast.error(e.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const rotateKey = async () => {
    if (dk?.has_key) {
      const ok = await new Promise((resolve) => {
        Modal.confirm({
          title: '重置密钥?',
          content: '重置后,旧的密钥立即失效。正在使用此密钥的闲鱼管家/自动发货平台会立刻断连,请先准备好在那边更新。',
          okText: '确认重置',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }
    setRotating(true);
    try {
      const r = await API.post('/api/admin/delivery-key/rotate');
      setNewKeyShown(r.data.data.key);
      loadDeliveryKey();
      Toast.success('已生成新密钥');
    } catch (e) {
      Toast.error(e.response?.data?.message || '生成失败');
    } finally {
      setRotating(false);
    }
  };

  const clearKey = async () => {
    const ok = await new Promise((resolve) => {
      Modal.confirm({
        title: '关闭对外发卡?',
        content: '关闭后 /api/delivery/* 会返回 401,所有依赖此密钥的自动发货会失败。密钥不会被删除,可随时重新打开。',
        okText: '确认关闭',
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    setClearing(true);
    try {
      await API.delete('/api/admin/delivery-key');
      Toast.success('已关闭,密钥已保留');
      setNewKeyShown(null);
      loadDeliveryKey();
    } catch (e) {
      Toast.error(e.response?.data?.message || '关闭失败');
    } finally {
      setClearing(false);
    }
  };

  // 重新启用已存在的密钥(不生成新密钥)
  const enableKey = async () => {
    setClearing(true);
    try {
      await API.put('/api/admin/delivery-key');
      Toast.success('已启用');
      loadDeliveryKey();
    } catch (e) {
      Toast.error(e.response?.data?.message || '启用失败');
    } finally {
      setClearing(false);
    }
  };

  const copy = (text, msg = '已复制') => {
    navigator.clipboard.writeText(text).then(
      () => Toast.success(msg),
      () => Toast.error('复制失败')
    );
  };

  if (loading) return <Spin spinning />;
  if (!data) return <div>加载失败</div>;

  const v = (k, fallback = '') => data[k] === undefined || data[k] === null ? fallback : data[k];

  // 通用紧凑表单行样式
  const row = { display: 'flex', alignItems: 'flex-start', marginBottom: 14 };
  const labelCell = { width: 120, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 };
  const inputCell = { flex: 1, minWidth: 0 };
  // 设置项说明:统一单行,放不下省略号截断(完整内容用 title 提示)
  const hint = { fontSize: 12, color: '#86909c', marginTop: 4, lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const card = { background: '#fff', borderRadius: 8, padding: '20px 24px', border: '1px solid #e5e6eb' };

  // Tab 1: 基本设置
  const renderBasic = () => (
    <div style={card}>
      <div style={row}>
        <label style={labelCell}>站点名称</label>
        <div style={inputCell}>
          <Input value={v('site_name')} onChange={(s) => setData({ ...data, site_name: s })} />
          <div style={hint}>浏览器标题栏与导航栏左侧显示</div>
        </div>
      </div>
      <div style={row}>
        <label style={labelCell}>对外地址</label>
        <div style={inputCell}>
          <Input
            value={v('public_url')}
            placeholder="https://api.yourdomain.com (留空用当前浏览器地址)"
            onChange={(s) => setData({ ...data, public_url: s })}
          />
          <div style={hint}>用于"接入指南"页生成配置时显示的 Base URL,末尾不要带 /</div>
        </div>
      </div>
      <div style={row}>
        <label style={labelCell}>首页公告</label>
        <div style={inputCell}>
          <TextArea
            rows={3}
            value={v('announcement')}
            placeholder="支持换行,过长会自动横向滚动显示"
            onChange={(s) => setData({ ...data, announcement: s })}
          />
          <div style={hint}>显示在首页导航栏上方,文字过长时自动横向滚动</div>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <Button type="primary" theme="solid" onClick={save} loading={saving}>保存设置</Button>
      </div>
    </div>
  );

  // Tab 1.5: 令牌默认值(本系统没有"用户"概念,额度直接挂在 token 上)
  const renderUserDefaults = () => (
    <div style={card}>
      <div style={row}>
        <label style={labelCell}>新建令牌默认额度</label>
        <div style={inputCell}>
          <InputNumber
            min={0} max={100000000}
            value={parseInt(v('default_user_quota', '0'), 10) || 0}
            onChange={(n) => setData({ ...data, default_user_quota: String(n ?? 0) })}
            style={{ width: 200 }}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          />
          <div style={hint} title="管理员手动创建令牌时不填额度的兜底值(通常从套餐 total_calls 取,此处只作 fallback)。0 表示不给默认额度,需要后续单独调整">
            手动建令牌且未指定额度时的兜底值,0 = 不给默认额度
          </div>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <Button type="primary" theme="solid" onClick={save} loading={saving}>保存</Button>
      </div>
    </div>
  );

  // Tab 2: 对外发卡 API
  const renderDeliveryApi = () => (
    <div>
      <div style={card}>
        {/* 状态 + 密钥 */}
        <div style={row}>
          <label style={labelCell}>对外发卡</label>
          <div style={inputCell}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Switch
                checked={!!dk?.enabled}
                loading={clearing || rotating}
                onChange={(b) => {
                  // 异步处理:避免在 Switch 同步渲染周期里同时 mount/unmount Modal,
                  // 否则会触发 React "Attempted to synchronously unmount a root" 警告
                  setTimeout(() => {
                    if (b) {
                      // 开启:如果没有密钥则生成,已有则只翻开关
                      if (!dk?.has_key) rotateKey();
                      else if (!dk?.enabled) {
                        // 已有密钥但被禁用 → 调 rotate 会顺便重新启用,这里改用直接 PUT enabled=1
                        enableKey();
                      }
                    } else {
                      // 关闭:保留密钥,只翻开关
                      clearKey();
                    }
                  }, 0);
                }}
              />
              <span style={{ fontSize: 13, color: dk?.enabled ? '#00b42a' : '#86909c', fontWeight: 500 }}>
                {dk ? (dk.enabled ? '● 已启用' : '○ 已关闭') : '加载中…'}
              </span>
              {dk && dk.stock !== undefined && (
                <span style={{ fontSize: 12, color: '#4e5969' }}>
                  可售库存 <strong style={{ color: dk.stock > 0 ? '#1d7dfa' : '#f56565' }}>{dk.stock}</strong> 张
                </span>
              )}
            </div>
            <div style={hint}>关闭后 /api/delivery/* 返回 401,所有外部自动发货会立刻断连</div>
          </div>
        </div>

        <div style={row}>
          <label style={labelCell}>发卡密钥</label>
          <div style={inputCell}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ background: '#f4f4f5', padding: '6px 10px', borderRadius: 4, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, color: dk?.has_key ? '#1f2329' : '#c9cdd4', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {dk?.has_key ? dk.masked : '(尚未生成)'}
              </code>
              {dk?.has_key && (
                <Button size="small" type="tertiary" onClick={() => copy(dk.masked, '已复制脱敏密钥')}>复制</Button>
              )}
            </div>
            <div style={hint} title="请求头 Authorization: Bearer 的值。密钥保存到 PostgreSQL 数据库,重启不会丢失">
              请求头 Authorization: Bearer 的值,存 PostgreSQL,重启不丢
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginLeft: 128 }}>
          <Button type="primary" theme="solid" onClick={rotateKey} loading={rotating}>
            {dk?.has_key ? '重置密钥' : '生成密钥'}
          </Button>
        </div>
      </div>

      {/* 接入说明 */}
      <div style={{ ...card, marginTop: 16, background: '#fafbfc' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#1f2329' }}>接入说明 (闲鱼超级管家)</div>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#4e5969', lineHeight: 1.8 }}>
          <li>在闲鱼超级管家「卡密库存」→「编辑卡密」→ 类型选 <strong>API接口</strong></li>
          <li>API 地址: <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 3, fontSize: 12 }}>{window.location.origin}/api/delivery/fetch-card</code></li>
          <li>请求方法: <strong style={{ color: '#f56565' }}>POST</strong> (GET 不记录订单信息)</li>
          <li>
            请求参数 JSON:<br />
            <code style={{ background: '#fff', padding: '4px 8px', borderRadius: 3, fontSize: 12, display: 'block', marginTop: 2 }}>{`{"plan":"套餐名","order_id":"{order_id}","buyer_id":"{buyer_id}"}`}</code>
          </li>
          <li>请求头: <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 3, fontSize: 12 }}>{`{"Authorization": "Bearer 发卡密钥"}`}</code></li>
          <li>响应 <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 3, fontSize: 12 }}>data</code> 直接是完整 sk-Key 字符串</li>
        </ol>
      </div>
    </div>
  );

  // Tab 3: 自动补货
  const renderAutoFill = () => (
    <div>
      <div style={card}>
        <div style={row}>
          <label style={labelCell}>自动补货</label>
          <div style={inputCell}>
            <Switch checked={draftAuto} onChange={(b) => setDraftAuto(b)} />
            <div style={hint} title={draftAuto
              ? '已开启:库存为 0 且调用方指定了 plan 时,按套餐模板自动生成新卡密再发货'
              : '未开启:库存为 0 时直接返回 OUT_OF_STOCK'}>
              {draftAuto
                ? '库存为 0 且指定了套餐时,自动生成新卡密再发货'
                : '未开启:库存为 0 时直接返回 OUT_OF_STOCK'}
            </div>
          </div>
        </div>

        <div style={row}>
          <label style={labelCell}>限速窗口</label>
          <div style={inputCell}>
            <InputNumber
              min={1} max={3600} value={draftWin}
              onChange={(v) => setDraftWin(v)}
              style={{ width: 140 }}
              disabled={!draftAuto}
            />
            <span style={{ fontSize: 12, color: '#86909c', marginLeft: 6 }}>秒</span>
            <div style={hint}>多长时间内统计一次自动新建次数,默认 60 秒</div>
          </div>
        </div>

        <div style={row}>
          <label style={labelCell}>窗口上限</label>
          <div style={inputCell}>
            <InputNumber
              min={1} max={1000} value={draftMax}
              onChange={(v) => setDraftMax(v)}
              style={{ width: 140 }}
              disabled={!draftAuto}
            />
            <span style={{ fontSize: 12, color: '#86909c', marginLeft: 6 }}>张</span>
            <div style={{ ...hint, display: 'flex', alignItems: 'center', gap: 4 }}
              title="窗口内最多自动新建多少张。超过后返回 OUT_OF_STOCK。">
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                窗口内最多自动新建多少张,超过返回 OUT_OF_STOCK,剩余额度:
              </span>
              <strong style={{ flexShrink: 0, color: (cfg?.rate_remaining ?? 0) > 0 ? '#1d7dfa' : '#f56565' }}>
                {cfg?.rate_remaining ?? '—'}
              </strong>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <Button type="primary" theme="solid" onClick={saveDeliveryConfig} loading={savingCfg}>
            保存补货配置
          </Button>
        </div>
      </div>

      {/* 可用套餐模板 */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>可用作自动补货模板的套餐</div>
        {cfg?.plans && cfg.plans.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {cfg.plans.map((p) => (
              <Tag
                key={p.id}
                color={p.enabled ? 'blue' : 'grey'}
                style={{ fontSize: 12, padding: '4px 10px' }}
              >
                {p.name} · {p.total_calls}次 · {p.enabled ? '已上架' : '已下架'}
              </Tag>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: '#86909c' }}>暂无套餐,请先到「套餐管理」创建</span>
        )}
        <div style={{ ...hint, marginTop: 8 }}>只有「已上架」的套餐才会被自动补货使用</div>
      </div>

      {/* 说明提示 */}
      <div style={{ marginTop: 16, padding: 14, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, fontSize: 12, color: '#ad6800', lineHeight: 1.8 }}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>⚠ 自动补货的卡密特征</div>
        <div>• <code style={{ background: '#fff', padding: '0 4px' }}>sold_to</code> 标记为 <code style={{ background: '#fff', padding: '0 4px' }}>auto-created</code> (手动发卡是 <code style={{ background: '#fff', padding: '0 4px' }}>auto-delivery</code>)</div>
        <div>• remain_quota / limit_5h / expire 等字段按套餐模板复制</div>
        <div>• 下架套餐即使名字匹配也不会补货</div>
      </div>
    </div>
  );

  // Tab 4: 数据维护(自动清理)
  const renderCleanup = () => {
    const ret = cleanupInfo?.retention || {};
    const pending = cleanupInfo?.pending || {};
    // 注意不能用 || :未激活卡保留天数合法值 0(永不清理)会被当成假值错误回退
    const int = (s, fb) => { const n = parseInt(s, 10); return Number.isFinite(n) ? n : fb; };
    return (
      <div>
        <div style={card}>
          <div style={row}>
            <label style={labelCell}>session 保留</label>
            <div style={inputCell}>
              <InputNumber
                min={1} max={365}
                value={int(v('session_retention_days', ret.session_days || 30), 30)}
                onChange={(n) => setData({ ...data, session_retention_days: String(n ?? 30) })}
                style={{ width: 140 }}
              />
              <span style={{ fontSize: 12, color: '#86909c', marginLeft: 6 }}>天</span>
              <div style={hint}>管理员登录 session 超过此天数自动失效</div>
            </div>
          </div>
          <div style={row}>
            <label style={labelCell}>日志保留</label>
            <div style={inputCell}>
              <InputNumber
                min={7} max={3650}
                value={int(v('log_retention_days', ret.log_days || 90), 90)}
                onChange={(n) => setData({ ...data, log_retention_days: String(n ?? 90) })}
                style={{ width: 140 }}
              />
              <span style={{ fontSize: 12, color: '#86909c', marginLeft: 6 }}>天</span>
              <div style={hint}>调用日志超过此天数自动删除,节省存储</div>
            </div>
          </div>
          <div style={row}>
            <label style={labelCell}>未激活卡</label>
            <div style={inputCell}>
              <InputNumber
                min={0} max={3650}
                value={int(v('unsold_token_retention_days', ret.unsold_token_days ?? 30), 30)}
                onChange={(n) => setData({ ...data, unsold_token_retention_days: String(n ?? 30) })}
                style={{ width: 140 }}
              />
              <span style={{ fontSize: 12, color: '#86909c', marginLeft: 6 }}>天</span>
              <div style={hint} title="已售出但买家一直未激活的卡,超过此天数自动回收;填 0 表示永不清理(待售库存卡不受此限制,永远不删)">
                已售出但未激活的卡,超过此天数自动回收,0 = 永不清理
              </div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Button type="primary" theme="solid" onClick={save} loading={saving}>保存保留天数</Button>
          </div>
        </div>

        <div style={{ ...card, marginTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>当前待清理量</div>
          {cleanupInfo ? (
            <div style={{ fontSize: 13, color: '#4e5969', lineHeight: 1.9 }}>
              <div>• 过期 session: <strong style={{ color: pending.sessions > 0 ? '#f56565' : '#00b42a' }}>{pending.sessions}</strong> 个</div>
              <div>• 超期日志: <strong style={{ color: pending.logs > 0 ? '#f56565' : '#00b42a' }}>{pending.logs}</strong> 条</div>
              <div>• 未激活卡: <strong style={{ color: pending.unused_tokens > 0 ? '#f56565' : '#00b42a' }}>{pending.unused_tokens}</strong> 张</div>
            </div>
          ) : (
            <span style={{ fontSize: 12, color: '#86909c' }}>加载中…</span>
          )}
          <div style={{ marginTop: 12 }}>
            <Button onClick={runCleanupNow} loading={cleanupRunning}>立即执行清理</Button>
            <span style={{ fontSize: 12, color: '#86909c', marginLeft: 10 }}>
              系统每 1 小时自动跑一次,也可手动触发
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Tab 6: 客服信息
  // Tab: 数据备份 — 定期快照令牌/用户/套餐,令牌有变化才落盘
  const renderBackup = () => {
    const fmtTime = (ts) => {
      if (!ts) return '从未备份';
      const d = new Date(Number(ts));
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
    };
    const fmtSize = (b) => {
      const n = Number(b || 0);
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
      return (n / 1024 / 1024).toFixed(2) + ' MB';
    };
    const int = (s, fb) => parseInt(s, 10) || fb;
    const triggerTag = (t) =>
      t === 'auto' ? <Tag color="blue" size="small">自动</Tag>
      : t === 'pre-restore' ? <Tag color="orange" size="small">恢复前快照</Tag>
      : <Tag color="green" size="small">手动</Tag>;

    const cols = [
      { title: '备份时间', dataIndex: 'created_at', render: (t) => fmtTime(t) },
      { title: '来源', dataIndex: 'trigger', render: (t) => triggerTag(t) },
      { title: '令牌数', dataIndex: 'token_count', render: (t) => Number(t || 0).toLocaleString() },
      { title: '大小', dataIndex: 'size_bytes', render: (t) => fmtSize(t) },
      {
        title: '文件状态', dataIndex: 'file_exists',
        render: (ok) => ok ? <Tag color="green" size="small">正常</Tag> : <Tag color="red" size="small">文件丢失</Tag>,
      },
      {
        title: '操作',
        render: (_, r) => (
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <Button size="small" theme="borderless" type="danger" disabled={!r.file_exists} onClick={() => setRestoreTarget(r)}>
              恢复
            </Button>
            <Button size="small" theme="borderless" type="danger" onClick={() => removeBackup(r)}>
              删除
            </Button>
          </span>
        ),
      },
    ];

    return (
      <div>
        {/* 概览:统计卡片 + 主操作,设置项收进「备份设置」弹窗 */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2329' }}>备份概览</div>
            <span style={{ display: 'inline-flex', gap: 8 }}>
              <Button onClick={() => setBackupCfgVisible(true)}>备份设置</Button>
              <Button type="primary" theme="solid" onClick={runBackupNow} loading={backupRunning}>立即备份</Button>
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {[
              { label: '当前令牌数', value: Number(backupOv?.token_count || 0).toLocaleString() },
              { label: '上次备份', value: fmtTime(backupOv?.last_at) },
              { label: '下次到期', value: backupOv?.next_due_at ? fmtTime(backupOv.next_due_at) : '—' },
              { label: '备份份数 / 占用', value: `${backupOv?.backup_count || 0} 份 · ${fmtSize(backupOv?.total_size_bytes)}` },
            ].map((s) => (
              <div key={s.label} style={{ background: '#f7f8fa', borderRadius: 6, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, color: '#86909c', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2329', whiteSpace: 'nowrap' }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14, fontSize: 12, color: '#86909c', lineHeight: 1.8 }}>
            <div>• 自动备份{v('backup_enabled', '1') === '1' ? '已开启' : '已关闭'};备份目录:{backupOv?.dir || '—'}</div>
            <div>• 快照含明文 API Key,请勿把备份目录放到公网可访问的位置</div>
          </div>
        </div>

        {/* 备份记录 */}
        <div style={{ ...card, marginTop: 16, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px 0', fontWeight: 600, fontSize: 14 }}>备份记录</div>
          {/* 卡片本身 padding 为 0(标题自带 24px 左内边距),这里给 Table 补上左右内边距,
              否则分页器「显示第 X 条」会紧贴卡片左边框,与标题左对齐不上 */}
          <div style={{ padding: '4px 24px 16px' }}>
            <Table
              columns={cols}
              dataSource={backupList}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              empty={<div style={{ padding: 40, textAlign: 'center', color: '#86909c' }}>暂无备份记录</div>}
            />
          </div>
        </div>

        {/* 备份策略设置 */}
        <Modal
          title="备份设置"
          visible={backupCfgVisible}
          onOk={saveBackupCfg}
          onCancel={() => setBackupCfgVisible(false)}
          okText="保存"
          cancelText="取消"
          confirmLoading={savingBackupCfg}
          width={520}
        >
          <div style={{ padding: '8px 0' }}>
            <div style={{ ...row, marginBottom: 18 }}>
              <label style={labelCell}>自动备份</label>
              <div style={inputCell}>
                <Switch
                  checked={v('backup_enabled', '1') === '1'}
                  onChange={(b) => setData({ ...data, backup_enabled: b ? '1' : '0' })}
                />
                <div style={{ ...hint, display: 'flex', alignItems: 'center', gap: 4 }}
                  title="系统每 6 小时检查一次,距上次备份超过设定天数且令牌数据相比上次有变化时才生成新备份;数据没动就跳过,避免堆一堆重复快照">
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    每 6 小时检查一次,超过设定天数且令牌有变化才备份
                  </span>
                  <strong style={{ flexShrink: 0, color: backupOv?.due ? '#f56565' : 'inherit' }}>
                    {backupOv?.changed_since_backup ? '(令牌已变化)' : '(数据未变则跳过)'}
                  </strong>
                </div>
              </div>
            </div>

            <div style={{ ...row, marginBottom: 18 }}>
              <label style={labelCell}>备份周期</label>
              <div style={inputCell}>
                <InputNumber
                  min={1} max={365}
                  value={int(v('backup_interval_days', '7'), 7)}
                  onChange={(n) => setData({ ...data, backup_interval_days: String(n ?? 7) })}
                  style={{ width: 140 }}
                />
                <span style={{ fontSize: 12, color: '#86909c', marginLeft: 6 }}>天</span>
                <div style={hint}>默认 7 天</div>
              </div>
            </div>

            <div style={row}>
              <label style={labelCell}>保留份数</label>
              <div style={inputCell}>
                <InputNumber
                  min={1} max={100}
                  value={int(v('backup_keep_count', '10'), 10)}
                  onChange={(n) => setData({ ...data, backup_keep_count: String(n ?? 10) })}
                  style={{ width: 140 }}
                />
                <span style={{ fontSize: 12, color: '#86909c', marginLeft: 6 }}>份</span>
                <div style={hint}>超出后自动清理最旧的快照(文件与记录一并删除)</div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  };

  // Tab: 配额重置(5h / 周) — 改完立刻作用于限流 + 用户面板统计
  // 5h 窗口:固定自然日内按 5 小时切分(0,5,10,15,20),到次日 00:00 强制重置(00-05 / 05-10 / 10-15 / 15-20 / 20-24)
  // 每周窗口:week_day(mon-sun) + week_hour(0-23)
  const renderReset = () => {
    const wkDay = v('quota_reset_week_day') || 'mon';
    const wkHour = parseInt(v('quota_reset_week_hour') || '0', 10);

    const dowLabel = ['周日','周一','周二','周三','周四','周五','周六'];
    const dowKey   = ['sun','mon','tue','wed','thu','fri','sat'];

    return (
      <div style={card}>
        <div style={{ ...row, marginBottom: 22 }}>
          <label style={labelCell}>5 小时窗口</label>
          <div style={inputCell}>
            <div style={{ fontSize: 13, color: '#1d2129' }}>固定自然日内按 5 小时切分</div>
            <div style={hint} title="每天切 5 段:00-05 / 05-10 / 10-15 / 15-20 / 20-24(次日 00:00 强制重置,最后一段 4 小时)">
              00-05 / 05-10 / 10-15 / 15-20 / 20-24,次日 00:00 强制重置
            </div>
          </div>
        </div>

        <div style={{ ...row, marginBottom: 22 }}>
          <label style={labelCell}>每周窗口起点</label>
          <div style={inputCell}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#4e5969' }}>每周</span>
              <Select
                value={wkDay}
                onChange={(v) => setData({ ...data, quota_reset_week_day: v })}
                style={{ width: 100 }}
              >
                {dowLabel.map((lab, i) => (
                  <Select.Option key={i} value={dowKey[i]}>{lab}</Select.Option>
                ))}
              </Select>
              <span style={{ fontSize: 12, color: '#4e5969' }}></span>
              <Select
                value={String(wkHour)}
                onChange={(v) => setData({ ...data, quota_reset_week_hour: String(v) })}
                style={{ width: 120 }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <Select.Option key={i} value={String(i)}>{String(i).padStart(2,'0')}:00</Select.Option>
                ))}
              </Select>
              <span style={{ fontSize: 12, color: '#4e5969' }}>重置</span>
            </div>
            <div style={hint}>例:周三 15:00 → 每周三下午 3 点周配额归零</div>
          </div>
        </div>

        <div style={{ marginTop: 4 }}>
          <Button type="primary" theme="solid" onClick={save} loading={saving}>保存</Button>
        </div>
      </div>
    );
  };

const renderSupport = () => (
    <div style={card}>
      <div style={row}>
        <label style={labelCell}>启用客服</label>
        <div style={inputCell}>
          <Switch
            checked={v('support_enabled') === '1'}
            onChange={(b) => setData({ ...data, support_enabled: b ? '1' : '0' })}
          />
          <div style={hint}>关闭后接入指南底部不会出现"联系客服"按钮</div>
        </div>
      </div>
      <div style={row}>
        <label style={labelCell}>显示内容</label>
        <div style={inputCell}>
          <TextArea
            rows={4}
            value={v('support_content')}
            placeholder="如:微信 xxx-abc;邮箱 support@example.com;或任意你想让用户看到的话"
            onChange={(s) => setData({ ...data, support_content: s })}
          />
          <div style={hint}>用户点"联系客服"按钮时弹窗显示这段内容,支持换行</div>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <Button type="primary" theme="solid" onClick={save} loading={saving}>保存</Button>
      </div>
    </div>
  );

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="line"
      >
        <TabPane tab="基本设置" itemKey="basic">
          <div style={{ paddingTop: 4 }}>{renderBasic()}</div>
        </TabPane>
        <TabPane tab="令牌默认值" itemKey="tokens">
          <div style={{ paddingTop: 4 }}>{renderUserDefaults()}</div>
        </TabPane>
        <TabPane tab="对外发卡" itemKey="delivery">
          <div style={{ paddingTop: 4 }}>{renderDeliveryApi()}</div>
        </TabPane>
        <TabPane tab="补货策略" itemKey="autofill">
          <div style={{ paddingTop: 4 }}>{renderAutoFill()}</div>
        </TabPane>
        <TabPane tab="数据维护" itemKey="cleanup">
          <div style={{ paddingTop: 4 }}>{renderCleanup()}</div>
        </TabPane>
        <TabPane tab="数据备份" itemKey="backup">
          <div style={{ paddingTop: 4 }}>{renderBackup()}</div>
        </TabPane>
        <TabPane tab="客服" itemKey="support">
          <div style={{ paddingTop: 4 }}>{renderSupport()}</div>
        </TabPane>
        <TabPane tab="配额重置" itemKey="reset">
          <div style={{ paddingTop: 4 }}>{renderReset()}</div>
        </TabPane>
      </Tabs>

      {/* 恢复备份确认弹窗 */}
      <Modal
        title="恢复备份"
        visible={!!restoreTarget}
        onCancel={() => setRestoreTarget(null)}
        onOk={doRestore}
        confirmLoading={backupRestoring}
        okText="确认恢复"
        cancelText="取消"
        okType="danger"
        width={460}
      >
        {restoreTarget && (
          <div style={{ padding: '8px 0', fontSize: 13, lineHeight: 1.8 }}>
            <div>将把 <b>{restoreTarget.filename}</b> 覆盖回当前数据库。</div>
            <div style={{ color: '#4e5969' }}>包含 {Number(restoreTarget.token_count || 0).toLocaleString()} 个令牌。</div>
            <div style={{ marginTop: 10, padding: 10, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, color: '#ad6800' }}>
              ⚠ 恢复会覆盖备份之后的所有令牌改动。系统会先自动生成一份「恢复前快照」,可随时回退。
            </div>
          </div>
        )}
      </Modal>

      {/* 一次性展示完整密钥的弹窗 */}
      <Modal
        title="请立即复制并保存新密钥"
        visible={!!newKeyShown}
        onOk={() => copy(newKeyShown || '', '密钥已复制,请妥善保存')}
        onCancel={() => setNewKeyShown(null)}
        okText="复制密钥"
        cancelText="关闭"
        width={560}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontSize: 13, color: '#f56565', marginBottom: 8, fontWeight: 500 }}>
            ⚠ 完整密钥只显示这一次,关掉这个弹窗后再也看不到(只能重置)
          </div>
          <div
            style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: 12, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, wordBreak: 'break-all', color: '#ad6800', fontWeight: 600, userSelect: 'all' }}
          >
            {newKeyShown}
          </div>
          <div style={{ fontSize: 12, color: '#86909c', marginTop: 10 }}>
            建议立刻在闲鱼管家更新此密钥,然后保存到密码管理器。复制后关闭此弹窗即可。
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminSettings;
